/**
 * Phase 41 Plan 41-05 Task 1 — TimeOffSummaryCard.
 *
 * Re-skinned extraction of the v6-era dashboard's TimeOffBalanceCard (that
 * dashboard page was retired in Phase 89). Same data path
 * (`getUserTimeOffs(year, state)`), v7 Card chrome, English-only copy.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Btn } from '../ui/Btn';
import { useAuth } from '../../services/authContext';
import { formatDayMonthShort } from '../../utils/dateFormat';
import { getUserTimeOffs } from '../../services/api/timeOff';
import { useMyVacationBalance } from '../../hooks/useTimeOff';
import {
  formatBalanceDays,
  formatDaysAndHours,
  hasVacationEntitlement,
  hoursToOwnDays,
} from '../../utils/vacationBalance';

const CURRENT_YEAR = new Date().getFullYear();
/**
 * Fallback quota, in notional 8 h days, for anyone the balance endpoint has no
 * entitlement for. It is a guess — the real figure is per-person and
 * FTE-prorated, which is exactly what MKS-326's balance provides for everyone
 * who has one.
 */
const ANNUAL_LEAVE_QUOTA = 25;

function fmtDate(s: string | null): string {
  if (!s) return '—';
  // MKS-351: `20 Nov` from the shared util — English month, no browser locale.
  return formatDayMonthShort(s);
}

function fmtRange(from: string | null, to: string | null): string {
  if (!from && !to) return '—';
  if (!to) return fmtDate(from);
  return `${fmtDate(from)} – ${fmtDate(to)}`;
}

export function TimeOffSummaryCard() {
  const { user } = useAuth();
  // UAT 2026-05-13 F-D5 — mirror Sidebar.tsx robustness fix: case-insensitive
  // + variant match so the gate doesn't quietly hide UI on a small string drift.
  const employeeTypeName = (user?.employeeType?.name ?? '').trim().toLowerCase();
  const isFreelance = employeeTypeName === 'freelance' || employeeTypeName === 'freelancer';

  const { data: approvedThisYear, isLoading } = useQuery({
    queryKey: ['dashboard', 'takenTimeOffs', CURRENT_YEAR],
    queryFn: () => getUserTimeOffs({ year: CURRENT_YEAR, state: 'approved' }),
  });
  const { data: pendingMine } = useQuery({
    queryKey: ['dashboard', 'pendingMyTimeOffs', CURRENT_YEAR],
    queryFn: () => getUserTimeOffs({ year: CURRENT_YEAR, state: 'submitted' }),
  });

  // MKS-326 — the real, FTE-prorated entitlement instead of a flat 25-day
  // guess. Without an entitlement: the card is exactly as it was.
  const { data: vacationBalance } = useMyVacationBalance(CURRENT_YEAR);
  const balance = hasVacationEntitlement(vacationBalance) ? vacationBalance : null;

  const today = new Date();
  const taken = (approvedThisYear ?? [])
    .filter((t) => t.dateTo && new Date(t.dateTo) < today)
    .reduce((s, t) => s + (t.totalDays ?? 0), 0);
  const planned = (approvedThisYear ?? [])
    .filter((t) => t.dateFrom && new Date(t.dateFrom) >= today)
    .reduce((s, t) => s + (t.totalDays ?? 0), 0);
  const submitted = pendingMine?.reduce((s, t) => s + (t.totalDays ?? 0), 0) ?? 0;
  // Days are day-length-agnostic, so the bar reads the same either way — only
  // the denominator changes from the guess to the person's own entitlement.
  const quotaDays = balance
    ? hoursToOwnDays(balance.entitlementHours + balance.carriedInHours, balance.dailyWorkingHours)
    : ANNUAL_LEAVE_QUOTA;
  const takenPct = quotaDays > 0 ? Math.min(100, (taken / quotaDays) * 100) : 0;
  const plannedPct =
    quotaDays > 0 ? Math.min(100 - takenPct, (planned / quotaDays) * 100) : 0;

  const upcoming = (approvedThisYear ?? [])
    .filter((t) => t.dateFrom && new Date(t.dateFrom) >= today)
    .slice(0, 2);

  return (
    <Card title={`Time off ${CURRENT_YEAR}`}>
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : (
        <>
          <div className="text-[12.5px] text-ms-text-secondary mb-3">
            {taken} taken · {planned} planned
          </div>
          {!isFreelance && (
            <div className="flex items-center gap-1 mb-3 h-2">
              <div
                className="h-full bg-emerald-400"
                style={{ width: `${takenPct}%`, borderRadius: '3px 0 0 3px' }}
              />
              <div
                className="h-full bg-amber-300"
                style={{ width: `${plannedPct}%`, borderRadius: '0 3px 3px 0' }}
              />
              <div className="flex-1 text-[10.5px] text-ms-text-muted tabular-nums text-right pl-2">
                {taken + planned} / {formatBalanceDays(quotaDays)} days
              </div>
            </div>
          )}
          {balance && (
            <div className="text-[11.5px] text-ms-text-secondary mb-3">
              Remaining: {formatDaysAndHours(balance.remainingHours, balance.dailyWorkingHours)}
              {balance.pendingHours > 0 && (
                <span className="text-ms-text-muted">
                  {' '}
                  · {formatDaysAndHours(balance.pendingHours, balance.dailyWorkingHours)} pending
                </span>
              )}
            </div>
          )}
          <div className="flex gap-3 text-[10.5px] text-ms-text-muted mb-3">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-emerald-400" />
              Taken {taken}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-amber-300" />
              Planned {planned}
            </span>
            {submitted > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-slate-300" />
                Pending {submitted}
              </span>
            )}
          </div>
          {upcoming.length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {upcoming.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-[11.5px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  <span className="text-ms-text-secondary flex-1 truncate">
                    {t.timeOffType?.name ?? 'Time off'} · {fmtRange(t.dateFrom, t.dateTo)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/time-off" className="block">
            <Btn variant="secondary" size="sm" className="w-full">
              Request time off
            </Btn>
          </Link>
        </>
      )}
    </Card>
  );
}
